import { lazy } from 'react';
import type { ModalRegistryItem } from '/shared/components/widgets/modal/Modal';

const modals: Record<string, ModalRegistryItem> = {
  CVS_SENT: {
    medium: true,
    Component: lazy(() => import('./CVSSentModal')),
  },
  TRIGGER_UNLOCK_REVIEW: {
    medium: true,
    submitOnEnter: true,
    Component: lazy(() => import('./TriggerUnlockReviewModal')),
  },
  BASKET_ITEM_INFO: {
    Component: lazy(() => import('./BasketItemInfoModal')),
  },
  BASKET_ITEM_DELETE: {
    Component: lazy(() => import('./BasketItemDeleteModal')),
  },
  BASKET_ITEM_DELETE_MULTIPLE: {
    Component: lazy(() => import('./BasketItemDeleteMultipleModal')),
  },
  CARBON_EMISSIONS_SUMMARY: {
    large: true,
    Component: lazy(() => import('./CarbonEmissionsSummaryModal')),
  },
  CONNECTIVITY_ERROR: {
    Component: lazy(() => import('./ConnectionErrorModal')),
  },
  CONFIRM_APPLICATION_STATE_CHANGE: {
    medium: true,
    Component: lazy(() => import('./ConfirmApplicationStateChangeModal')),
  },
  CONFIRM_REDUCTION_GOAL_CHANGE: {
    medium: true,
    Component: lazy(() => import('./ConfirmReductionGoalChangeModal')),
  },
  CONFIRM_APPLICATION_DATES: {
    medium: true,
    Component: lazy(() => import('./ConfirmApplicationDatesModal')),
  },
  CONFIRM_APPLICATION_OFFER: {
    medium: true,
    Component: lazy(() => import('./ConfirmApplicationDeclareOfferModal')),
  },
  CONFIRM_APPLICATION_COUNTRY: {
    medium: true,
    Component: lazy(() => import('./ConfirmApplicationCountryModal')),
  },
  CONFIRM_APPLICATION_INTENSITY_METRIC: {
    large: true,
    Component: lazy(() => import('./ConfirmApplicationIntensityMetricModal')),
  },
  CONFIRM_RESEND_REVOKE_REMOVE_ASSOCIATED_USER: {
    medium: true,
    Component: lazy(() => import('./ConfirmResendRevokeRemoveAssociatedUser')),
  },
  CONFIRM_REASSIGN_ASSOCIATED_USER: {
    medium: true,
    Component: lazy(() => import('./ConfirmReassignAssociatedUser')),
  },
  EMISSION_REDUCTION_EXAMPLES: {
    medium: true,
    Component: lazy(() => import('./EmissionReductionExamplesModal')),
  },
  UNKNOWN_ERROR: {
    Component: lazy(() => import('./UnknownErrorModal')),
  },
  USER_CHANGE_LANG: {
    medium: true,
    Component: lazy(() => import('./UserChangeLangModal')),
  },
  APPLICATION_CREATE_CHANGE_LANG: {
    medium: true,
    Component: lazy(() => import('./ApplicationChangeLangModal')),
  },
  APPLICATION_SIGNATURE: {
    medium: true,
    icon: 'envelope',
    Component: lazy(() => import('./ApplicationSignatureModal')),
  },
  APPLICATION_SUBMITTED: {
    medium: true,
    icon: 'envelope',
    Component: lazy(() => import('./ApplicationSubmittedModal')),
  },
  TEMPLATE_DOWNLOAD: {
    medium: true,
    Component: lazy(() => import('./TemplateDownloadModal')),
  },
  MOBILE_WARNING: {
    icon: 'laptop',
    responsive: true,
    // Set the z-index to be above the cookie preferences panel,
    // as the user would not yet have a chance to opt in/out
    zIndex: 1002,
    Component: lazy(() => import('./MobileWarningModal')),
  },
  SAVE_BASKET_UPDATES: {
    medium: true,
    Component: lazy(() => import('./SaveBasketUpdatesModal')),
  },
  EXPLAINER_CARBON_INTENSITY: {
    medium: true,
    Component: lazy(() => import('./ExplainerCarbonIntensityModal')),
  },
  EXPLAINER_ON_THE_GO_CHARGE: {
    medium: true,
    Component: lazy(() => import('./ExplainerOnTheGoChargeModal')),
  },
  LEGACY_PAGE_WARNING: {
    medium: true,
    Component: lazy(() => import('./LegacyPageModal')),
  },
  REDUCTION_BELOW_MINIMUM: {
    medium: true,
    Component: lazy(() => import('./ReductionBelowMinimumModal')),
  },
  REDUCTION_MIGRATION: {
    medium: true,
    Component: lazy(() => import('./ReductionMigrationModal')),
  },
  EXPLAINER_PROJECTIONS: {
    extraLarge: true,
    hasInputField: true,
    isTopAlign: true,
    Component: lazy(() => import('./ExplainerProjectionsModal')),
  },
  PAYMENT_PROBLEM: {
    medium: true,
    Component: lazy(() => import('./PaymentProblemModal')),
  },
  CMP_USER_NOTIFICATION: {
    medium: true,
    Component: lazy(() => import('./UserNotificationModal')),
  },
  OFFSET_NO_CONTRIBUTION: {
    medium: true,
    Component: lazy(() => import('./OffsetNoContributionModal')),
  },
  OFFSET_WITH_CONTRIBUTION: {
    medium: true,
    Component: lazy(() => import('./OffsetWithContributionModal')),
  },
  OFFSET_OVERRIDE_NO_OFFSET: {
    medium: true,
    Component: lazy(() => import('./OffsetOverrideNoOffsetModal')),
  },
  OFFSET_OVERRIDE_UNLOCK: {
    medium: true,
    Component: lazy(() => import('./OffsetOverrideUnlockModal')),
  },
  PAYMENT_CANCEL_OFFSET: {
    medium: true,
    Component: lazy(() => import('./PaymentPageCancelOffsetModal')),
  },
  UNLOCK_STEP_2: {
    medium: true,
    submitOnEnter: true,
    Component: lazy(() => import('./UnlockStep2Modal')),
  },
  UNLOCK_STEP_2_INFO: {
    medium: true,
    Component: lazy(() => import('./UnlockStep2ModalInfo')),
  },
  UP_FRONT_DISCLAIMER: {
    large: true,
    Component: lazy(() => import('./UpFrontDisclaimer')),
  },
  HOTSPOTS_CARBON_EMISSIONS: {
    medium: true,
    Component: lazy(() => import('./HotspotsCarbonEmissionModal')),
  },
  HOTSPOTS_SCOPE_BREAKDOWN: {
    medium: true,
    Component: lazy(() => import('./HotspotsScopeBreakdownModal')),
  },
  HOTSPOTS_EMISSIONS_HOTSPOTS: {
    medium: true,
    Component: lazy(() => import('./HotspotsEmissionsHotspotsModal')),
  },
  HOTSPOTS_EMISSIONS_BREAKDOWN: {
    medium: true,
    Component: lazy(() => import('./HotspotsEmissionsBreakdownModal')),
  },
  HOTSPOTS_EHS_BREAKDOWN: {
    medium: true,
    Component: lazy(() => import('./HotspotsEHSBreakdownModal')),
  },
  UNIT_PREF_MISMATCH: {
    medium: true,
    Component: lazy(() => import('./UnitPrefMismatchModal')),
  },
  ADD_INITIATIVE_CUSTOM: {
    large: true,
    hasInputField: true,
    rightAlign: true,
    pageReplace: true,
    Component: lazy(() => import('./AddCustomInitiativeModal')),
  },
  ATTACH_OR_EDIT_INITIATIVE: {
    large: true,
    isWhiteCloseIcon: true,
    hasInputField: true,
    rightAlign: true,
    pageReplace: true,
    Component: lazy(() => import('./AttachOrEditInitiativeModal')),
  },
  DELETE_INITIATIVE: {
    medium: true,
    Component: lazy(() => import('./DeleteInitiativeModal')),
  },
  DASHBOARD_EXPLAINER: {
    medium: true,
    Component: lazy(() => import('./DashboardExplainerModal')),
  },
  REQUEST_CONFIRM_OF_SAVE: {
    medium: true,
    Component: lazy(() => import('./RequestConfirmationOfSaveFromUserModal')),
  },
  QUESTION_SUBMISSION_CONFIRMATION: {
    medium: true,
    Component: lazy(() => import('./QuestionSubmissionConfirmationModal')),
  },
  DELETION_WARNING: {
    small: true,
    Component: lazy(() => import('./BasketDeletionWarning')),
  },
  MEASURING_CARBON_INTENSITY: {
    medium: true,
    Component: lazy(() => import('./MeasuringCarbonIntensityModal')),
  },
  EDUCATION: {
    large: true,
    rightAlign: true,
    pageReplace: true,
    Component: lazy(() => import('./EducationModal')),
  },
  ANNUAL_UPDATES_ENABLE_CONFIRMATION: {
    medium: true,
    Component: lazy(() => import('./annual-updates/AnnualUpdatesEnableConfirmationModal')),
  },
  ANNUAL_UPDATES_LEARN_MORE: {
    medium: true,
    Component: lazy(() => import('./annual-updates/AnnualUpdatesLearnMoreModal')),
  },
  CHANGE_BASELINE_PERIOD: {
    title: null,
    large: true,
    rightAlign: true,
    pageReplace: true,
    isBlockingWithCloseButton: true,
    Component: lazy(() => import('./ChangeBaselinePeriodModal')),
  },
  COMPARISON_EMISSIONS: {
    medium: true,
    Component: lazy(() => import('./ComparisonEmissionsModal')),
  },
  COMPARISON_SCOPE: {
    medium: true,
    Component: lazy(() => import('./ComparisonScopeModal')),
  },
  COMPARISON_CATEGORY: {
    medium: true,
    Component: lazy(() => import('./ComparisonCategoryModal')),
  },
};

export default modals;
